// The Agents place (technical design §4.5, §8.3–8.4; the owner-agents prototype): the owner's view of
// the AI agents, the inbox of what they asked a person to decide, what each may do, and the chats
// with the owner analyst (the owner's) and the support agent. The owner and the manager see the
// agents (agents.view); a dispatcher or bookkeeper sees the inbox and the support chat.
// BL-189: its parts are PageHead's tabs, each part on its pattern under the one head: the agents on
// DashboardPage (Overview.tsx), Approvals on ListPage (shared/ApprovalRequests.tsx, the books' too), the
// chats on RowList and Composer (Chat.tsx). Agent settings are Setup's now, so their tab says where they
// went.

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from '@flowology/ui-web/fl';
import { ListPage, PageHead, type PageHeadProps } from '@flowology/ui-web/patterns';
import { PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import { useQuery, useToast } from '../books/ui.tsx';
import { liveStream } from '../connection/live.ts';
import { AGENT_EVENTS } from './model.ts';
import { useApprovalRequests } from '../shared/ApprovalRequests.tsx';
import { Chat } from './Chat.tsx';
import { Overview } from './Overview.tsx';
import type { AgentsPart, AgentsProps } from './props.ts';
import type { AgentsList } from './types.ts';
import './agents-page.css';

/** Who sees the agents themselves: whoever holds agents.view (the owner and the manager). */
const VIEWS_AGENTS = 'agents.view';

// Each part's title and its one line, in the head over the tabs.
const HEAD: Record<AgentsPart, [string, string]> = {
  overview: [
    'AI agents',
    'Each agent works under a role and uses only that role’s actions; every tool call is checked and audited.',
  ],
  approvals: [
    'Approvals',
    'Agents ask before they contact a customer, book work or touch money. Approving runs it as the agent, with your name.',
  ],
  settings: [
    'Agent settings',
    'For each action: automatic, ask first or never. Tighten anything; loosen only up to its maximum.',
  ],
  analyst: [
    'Ask the analyst',
    'Questions about the business, answered from the reports with the queries it ran. It only reads.',
  ],
  support: [
    'Support',
    'How to do something in Flowology. A change it suggests waits for the owner’s approval.',
  ],
};

/** Approvals: the agent requests on ListPage, under the screen's one head (its title and parts). */
function ApprovalsPart({
  head,
  said,
  ...input
}: Parameters<typeof useApprovalRequests>[0] & { head: PageHeadProps; said: ReactNode }) {
  return <ListPage head={head} notice={said} {...useApprovalRequests(input)} />;
}

export function AgentsScreen({
  nav,
  timeZone,
  open,
}: {
  nav: NavProps;
  timeZone?: string;
  /** A notification opens a part (Support, say). */
  open?: { part: AgentsPart; at: number };
}) {
  const seesAgents = (nav.permissions ?? []).includes(VIEWS_AGENTS);
  const [part, setPart] = useState<AgentsPart>(open?.part ?? (seesAgents ? 'overview' : 'approvals'));
  useEffect(() => {
    if (open) setPart(open.part);
  }, [open?.part, open?.at]);
  const [toast, setToast] = useToast();
  const pending = useQuery<{ approvals: unknown[] }>('approvals.list', { status: 'pending' }, [part]);
  const list = useQuery<AgentsList>('agents.list', seesAgents ? {} : null, [part]);
  // a decision made on another screen, or an agent stopped there, re-reads the counts (QA-860)
  const reread = useRef<() => void>(() => undefined);
  reread.current = () => {
    void pending.reload();
    void list.reload();
  };
  useEffect(() => {
    const es = liveStream();
    let timer: ReturnType<typeof setTimeout> | undefined;
    es.addEventListener('change', (e) => {
      const { type } = JSON.parse(e.data) as { type: string };
      if (!AGENT_EVENTS.test(type)) return;
      clearTimeout(timer);
      timer = setTimeout(() => reread.current(), 250);
    });
    return () => {
      clearTimeout(timer);
      es.close();
    };
  }, []);
  const stopped = new Map(list.data?.agents.map((a) => [a.key, a.stoppedReason]) ?? []);
  const crumb = usePageCrumb(nav);
  const props: AgentsProps = {
    isOwner: (nav.permissions ?? []).includes('agents.manage'),
    seesAgents,
    ...(timeZone ? { timeZone } : {}),
    toast: setToast,
    agentKey: 'receptionist',
    go: setPart,
  };
  const waiting = pending.data?.approvals.length ?? 0;
  const parts: { part: AgentsPart; label: string; count?: number }[] = [
    ...(seesAgents ? [{ part: 'overview' as const, label: 'Agents' }] : []),
    { part: 'approvals', label: 'Approvals', count: waiting },
    ...(seesAgents ? [{ part: 'settings' as const, label: 'Agent settings' }] : []),
    ...(props.isOwner ? [{ part: 'analyst' as const, label: 'Ask the analyst' }] : []),
    { part: 'support', label: 'Support' },
  ];
  const [title, lede] = HEAD[part];
  const head: PageHeadProps = {
    ...(crumb ? { crumb } : {}),
    title,
    lede,
    tabs: {
      label: 'Agents',
      items: parts.map((p) => ({
        label: p.label,
        isCurrent: p.part === part,
        onPress: () => setPart(p.part),
        ...(p.count ? { count: p.count } : {}),
      })),
    },
  };
  // what was just done, said once under the head (it clears itself)
  const said = (
    <p className="agents-said" role="status">
      {toast}
    </p>
  );
  return (
    <div className="app">
      <main className="agents-page">
        <PageBanners nav={nav} />
        {part === 'overview' && seesAgents && <Overview {...props} head={head} said={said} />}
        {part === 'approvals' && (
          <ApprovalsPart
            head={head}
            said={said}
            toast={setToast}
            stoppedReason={(k) => stopped.get(k) ?? null}
            onChange={() => void pending.reload()}
            {...(timeZone ? { timeZone } : {})}
          />
        )}
        {part === 'settings' && seesAgents && (
          <>
            <PageHead {...head} />
            <p className="agents-moved" role="note">
              Agent settings moved to <Link href="#/settings/agents">Setup › AI agents</Link>.
            </p>
          </>
        )}
        {part === 'analyst' && props.isOwner && (
          <Chat
            props={props}
            head={head}
            said={said}
            agentKey="analyst"
            examples={['How is this month going?', 'Who owes us money?']}
          />
        )}
        {part === 'support' && (
          <Chat
            props={props}
            head={head}
            said={said}
            agentKey="support"
            examples={['How long after a job is the rating text sent?', 'Where do I set the trip charge?']}
          />
        )}
      </main>
    </div>
  );
}
